export type WorkspaceRequestCategory =
  | 'metadata'
  | 'permissions'
  | 'permission-graph'
  | 'admin-diagnostics'
  | 'workspace-api'
  | 'ai-bootstrap'
  | 'settings'
  | 'foreground-action';

export type WorkspaceRequestPriority = 'foreground' | 'visible' | 'background';
export type WorkspaceRequestMode = 'foreground' | 'background';

export interface WorkspaceRequestDescriptor<T> {
  workspaceId: string;
  category: WorkspaceRequestCategory;
  key: string;
  priority: WorkspaceRequestPriority;
  mode: WorkspaceRequestMode;
  signal?: AbortSignal;
  run: (signal: AbortSignal) => Promise<T>;
}

export interface WorkspaceBatchRequestDescriptor<Item, Result> {
  workspaceId: string;
  category: WorkspaceRequestCategory;
  key: string;
  priority: WorkspaceRequestPriority;
  mode: WorkspaceRequestMode;
  signal?: AbortSignal;
  items: readonly Item[];
  itemKey: (item: Item) => string;
  maxBatchSize: number;
  run: (
    items: readonly Item[],
    signal: AbortSignal
  ) => Promise<Record<string, Result>>;
}

export interface RequestCoordinatorPort {
  execute<T>(descriptor: WorkspaceRequestDescriptor<T>): Promise<T>;
  executeBatch<Item, Result>(
    descriptor: WorkspaceBatchRequestDescriptor<Item, Result>
  ): Promise<Record<string, Result>>;
  abortByWorkspace(workspaceId: string): void;
  markDegraded(
    workspaceId: string,
    category: WorkspaceRequestCategory,
    reason: unknown
  ): void;
}

export interface RequestCoordinatorMetrics {
  queued: number;
  started: number;
  completed: number;
  failed: number;
  deduped: number;
  batchFlushes: number;
  aborted: number;
  backoffSignals: number;
  queueDepth: number;
  active: number;
}

export interface RequestBackoffSignal {
  retryAfterMs?: number;
  status?: number;
  headers?: Pick<Headers, 'get'>;
}

interface CoordinatorTask<T> {
  descriptor: WorkspaceRequestDescriptor<T>;
  controller: AbortController;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
  enqueuedAt: number;
}

interface InflightRequest<T> {
  promise: Promise<T>;
  controller: AbortController;
  consumers: Set<symbol>;
}

interface BackoffState {
  until: number;
  failures: number;
}

interface PendingBatch<Item, Result> {
  descriptor: WorkspaceBatchRequestDescriptor<Item, Result>;
  items: Map<string, Item>;
  waiters: Array<{
    keys: string[];
    resolve: (value: Record<string, Result>) => void;
    reject: (error: unknown) => void;
  }>;
}

const DEFAULT_MAX_CONCURRENCY = 4;
const CATEGORY_MAX_CONCURRENCY: Partial<
  Record<WorkspaceRequestCategory, number>
> = {
  permissions: 6,
  'permission-graph': 1,
  'admin-diagnostics': 1,
  metadata: 2,
  'ai-bootstrap': 2,
};
const PRIORITY_ORDER: Record<WorkspaceRequestPriority, number> = {
  foreground: 0,
  visible: 1,
  background: 2,
};
const DEFAULT_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

export class RequestCoordinator implements RequestCoordinatorPort {
  private readonly queues = new Map<string, CoordinatorTask<unknown>[]>();
  private readonly activeCount = new Map<string, number>();
  private readonly inflight = new Map<string, InflightRequest<unknown>>();
  private readonly backoff = new Map<string, BackoffState>();
  private readonly backoffTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private readonly pendingBatches = new Map<
    string,
    PendingBatch<unknown, unknown>
  >();
  private readonly metrics: Omit<
    RequestCoordinatorMetrics,
    'queueDepth' | 'active'
  > = {
    queued: 0,
    started: 0,
    completed: 0,
    failed: 0,
    deduped: 0,
    batchFlushes: 0,
    aborted: 0,
    backoffSignals: 0,
  };

  execute<T>(descriptor: WorkspaceRequestDescriptor<T>): Promise<T> {
    const key = requestKey(descriptor);
    const existing = this.inflight.get(key) as InflightRequest<T> | undefined;
    if (existing && !existing.controller.signal.aborted) {
      this.metrics.deduped++;
      return this.subscribe(existing, descriptor.signal);
    }
    if (existing) {
      this.inflight.delete(key);
    }

    const controller = new AbortController();
    if (descriptor.signal?.aborted) {
      return Promise.reject(descriptor.signal.reason);
    }
    let resolveTask!: (value: T) => void;
    let rejectTask!: (error: unknown) => void;
    let inflight!: InflightRequest<T>;
    const promise = new Promise<T>((resolve, reject) => {
      resolveTask = resolve;
      rejectTask = reject;
    }).finally(() => {
      if (this.inflight.get(key) === inflight) {
        this.inflight.delete(key);
      }
    });

    const task: CoordinatorTask<T> = {
      descriptor,
      controller,
      resolve: resolveTask,
      reject: rejectTask,
      enqueuedAt: Date.now(),
    };
    const budget = budgetKey(descriptor.workspaceId, descriptor.category);
    const queue = this.queues.get(budget) ?? [];
    queue.push(task as CoordinatorTask<unknown>);
    queue.sort(compareTaskPriority);
    this.queues.set(budget, queue);
    this.metrics.queued++;
    inflight = {
      promise: promise as Promise<unknown>,
      controller,
      consumers: new Set(),
    } as InflightRequest<T>;
    this.inflight.set(key, inflight as InflightRequest<unknown>);
    this.drain(budget);

    return this.subscribe(inflight, descriptor.signal);
  }

  executeBatch<Item, Result>(
    descriptor: WorkspaceBatchRequestDescriptor<Item, Result>
  ): Promise<Record<string, Result>> {
    const key = requestKey(descriptor);
    let pending = this.pendingBatches.get(key) as
      | PendingBatch<Item, Result>
      | undefined;
    const isNewBatch = !pending;
    if (!pending) {
      pending = {
        descriptor,
        items: new Map(),
        waiters: [],
      };
      this.pendingBatches.set(key, pending as PendingBatch<unknown, unknown>);
    }

    const keys = descriptor.items.map(item => {
      const itemKey = descriptor.itemKey(item);
      pending?.items.set(itemKey, item);
      return itemKey;
    });
    const result = new Promise<Record<string, Result>>((resolve, reject) => {
      pending?.waiters.push({ keys, resolve, reject });
    });
    if (isNewBatch) {
      queueMicrotask(() => {
        void this.flushBatch<Item, Result>(key);
      });
    }
    return raceWithSignal(result, descriptor.signal);
  }

  abortByWorkspace(workspaceId: string): void {
    for (const [key, request] of this.inflight) {
      if (key.startsWith(`${workspaceId}:`)) {
        this.metrics.aborted++;
        request.controller.abort(
          new DOMException('Workspace aborted', 'AbortError')
        );
      }
    }

    for (const [budget, queue] of this.queues) {
      if (!budget.endsWith(`:${workspaceId}`)) continue;
      for (const task of queue) {
        task.controller.abort(
          new DOMException('Workspace aborted', 'AbortError')
        );
        task.reject(task.controller.signal.reason);
      }
      this.queues.set(budget, []);
      this.clearBackoffTimer(budget);
    }

    for (const [key, batch] of this.pendingBatches) {
      if (!key.startsWith(`${workspaceId}:`)) continue;
      const error = new DOMException('Workspace aborted', 'AbortError');
      for (const waiter of batch.waiters) {
        waiter.reject(error);
      }
      this.pendingBatches.delete(key);
    }
  }

  markDegraded(
    workspaceId: string,
    category: WorkspaceRequestCategory,
    reason: unknown
  ): void {
    this.metrics.backoffSignals++;
    const budget = budgetKey(workspaceId, category);
    const previous = this.backoff.get(budget);
    const retryAfterMs = parseRetryAfterMs(reason);
    const fallbackMs = Math.min(
      DEFAULT_BACKOFF_MS * Math.pow(2, previous?.failures ?? 0),
      MAX_BACKOFF_MS
    );
    const delayMs = retryAfterMs ?? fallbackMs;
    this.backoff.set(budget, {
      until: Date.now() + Math.min(delayMs, MAX_BACKOFF_MS),
      failures: (previous?.failures ?? 0) + 1,
    });
    this.drain(budget);
  }

  resetForTests(): void {
    for (const request of this.inflight.values()) {
      request.controller.abort(
        new DOMException('Coordinator reset', 'AbortError')
      );
    }
    for (const queue of this.queues.values()) {
      for (const task of queue) {
        task.controller.abort(
          new DOMException('Coordinator reset', 'AbortError')
        );
        task.reject(task.controller.signal.reason);
      }
    }
    this.queues.clear();
    this.activeCount.clear();
    this.inflight.clear();
    this.backoff.clear();
    for (const timer of this.backoffTimers.values()) clearTimeout(timer);
    this.backoffTimers.clear();
    for (const batch of this.pendingBatches.values()) {
      const error = new DOMException('Coordinator reset', 'AbortError');
      for (const waiter of batch.waiters) {
        waiter.reject(error);
      }
    }
    this.pendingBatches.clear();
    for (const key of Object.keys(this.metrics) as Array<
      keyof typeof this.metrics
    >) {
      this.metrics[key] = 0;
    }
  }

  getMetricsSnapshot(): RequestCoordinatorMetrics {
    return {
      ...this.metrics,
      queueDepth: [...this.queues.values()].reduce(
        (total, queue) => total + queue.length,
        0
      ),
      active: [...this.activeCount.values()].reduce(
        (total, count) => total + count,
        0
      ),
    };
  }

  private async flushBatch<Item, Result>(key: string): Promise<void> {
    const pending = this.pendingBatches.get(key) as
      | PendingBatch<Item, Result>
      | undefined;
    if (!pending) {
      return;
    }
    this.pendingBatches.delete(key);
    this.metrics.batchFlushes++;
    const entries = [...pending.items.entries()];
    const results: Record<string, Result> = {};
    const maxBatchSize = Math.max(
      1,
      Math.floor(pending.descriptor.maxBatchSize)
    );
    try {
      for (let offset = 0; offset < entries.length; offset += maxBatchSize) {
        const chunk = entries.slice(offset, offset + maxBatchSize);
        const chunkKeys = chunk.map(([itemKey]) => itemKey);
        const chunkResult = await this.execute({
          workspaceId: pending.descriptor.workspaceId,
          category: pending.descriptor.category,
          key: `${pending.descriptor.key}:batch:${stableBatchKey(chunkKeys)}`,
          priority: pending.descriptor.priority,
          mode: pending.descriptor.mode,
          run: signal =>
            pending.descriptor.run(
              chunk.map(([, item]) => item),
              signal
            ),
        });
        Object.assign(results, chunkResult);
      }
      for (const waiter of pending.waiters) {
        waiter.resolve(selectBatchResults(results, waiter.keys));
      }
    } catch (error) {
      for (const waiter of pending.waiters) {
        waiter.reject(error);
      }
    }
  }

  private drain(budget: string): void {
    const queue = this.queues.get(budget);
    if (!queue?.length) return;

    const limit = maxConcurrencyForBudget(budget);
    while ((this.activeCount.get(budget) ?? 0) < limit && queue.length) {
      const task = queue[0];
      if (!task) return;
      const delayMs = this.delayFor(task.descriptor);
      if (delayMs > 0 && task.descriptor.priority !== 'foreground') {
        this.scheduleDrain(budget, delayMs);
        return;
      }
      queue.shift();
      this.activeCount.set(budget, (this.activeCount.get(budget) ?? 0) + 1);
      void this.runTask(budget, task);
    }
  }

  private async runTask(
    budget: string,
    task: CoordinatorTask<unknown>
  ): Promise<void> {
    this.metrics.started++;
    try {
      if (task.controller.signal.aborted) {
        throw task.controller.signal.reason;
      }
      const result = await task.descriptor.run(task.controller.signal);
      this.backoff.delete(budget);
      this.clearBackoffTimer(budget);
      this.metrics.completed++;
      task.resolve(result);
    } catch (error) {
      this.metrics.failed++;
      task.reject(error);
    } finally {
      this.activeCount.set(
        budget,
        Math.max(0, (this.activeCount.get(budget) ?? 1) - 1)
      );
      this.drain(budget);
    }
  }

  private delayFor(descriptor: WorkspaceRequestDescriptor<unknown>): number {
    const state = this.backoff.get(
      budgetKey(descriptor.workspaceId, descriptor.category)
    );
    if (!state) return 0;
    return Math.max(0, state.until - Date.now());
  }

  private scheduleDrain(budget: string, delayMs: number): void {
    if (this.backoffTimers.has(budget)) return;
    const timer = setTimeout(() => {
      this.backoffTimers.delete(budget);
      this.drain(budget);
    }, delayMs);
    this.backoffTimers.set(budget, timer);
  }

  private clearBackoffTimer(budget: string): void {
    const timer = this.backoffTimers.get(budget);
    if (timer) clearTimeout(timer);
    this.backoffTimers.delete(budget);
  }

  private subscribe<T>(
    inflight: InflightRequest<T>,
    signal?: AbortSignal
  ): Promise<T> {
    if (signal?.aborted) return Promise.reject(signal.reason);
    const consumer = Symbol('request-consumer');
    inflight.consumers.add(consumer);

    return new Promise<T>((resolve, reject) => {
      let settled = false;
      const finish = () => {
        if (settled) return false;
        settled = true;
        signal?.removeEventListener('abort', onAbort);
        inflight.consumers.delete(consumer);
        return true;
      };
      const onAbort = () => {
        if (!finish()) return;
        reject(signal?.reason);
        if (inflight.consumers.size === 0) {
          inflight.controller.abort(signal?.reason);
        }
      };
      signal?.addEventListener('abort', onAbort, { once: true });
      inflight.promise.then(
        value => {
          if (finish()) resolve(value);
        },
        error => {
          if (finish()) reject(error);
        }
      );
    });
  }
}

export const defaultRequestCoordinator = new RequestCoordinator();

export function parseRetryAfterMs(reason: unknown): number | undefined {
  const headerRetryAfter = readRetryAfterHeader(reason);
  if (headerRetryAfter !== undefined) {
    return parseRetryAfterSecondsOrDate(headerRetryAfter);
  }

  const retryAfterMs = readRetryAfterMsValue(reason);
  if (retryAfterMs !== undefined) {
    return clampRetryAfterMs(retryAfterMs);
  }

  const retryAfter = readRetryAfterValue(reason);
  if (retryAfter !== undefined) {
    return parseRetryAfterSecondsOrDate(retryAfter);
  }

  return undefined;
}

function parseRetryAfterSecondsOrDate(
  retryAfter: string | number
): number | undefined {
  if (typeof retryAfter === 'number') {
    return Number.isFinite(retryAfter)
      ? Math.max(0, retryAfter * 1000)
      : undefined;
  }

  const trimmed = retryAfter.trim();
  const seconds = Number(trimmed);
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }
  const date = Date.parse(trimmed);
  if (Number.isFinite(date)) {
    return Math.max(0, date - Date.now());
  }
  return undefined;
}

function clampRetryAfterMs(retryAfterMs: string | number): number | undefined {
  const ms = Number(retryAfterMs);
  return Number.isFinite(ms) ? Math.max(0, ms) : undefined;
}

function requestKey(descriptor: {
  workspaceId: string;
  category: WorkspaceRequestCategory;
  key: string;
}): string {
  return `${descriptor.workspaceId}:${descriptor.category}:${descriptor.key}`;
}

function selectBatchResults<Result>(
  results: Record<string, Result>,
  keys: string[]
): Record<string, Result> {
  const selected: Record<string, Result> = {};
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(results, key)) {
      selected[key] = results[key] as Result;
    }
  }
  return selected;
}

function stableBatchKey(keys: string[]): string {
  return [...keys].sort().map(encodeURIComponent).join(',');
}

function budgetKey(
  workspaceId: string,
  category: WorkspaceRequestCategory
): string {
  return `${category}:${workspaceId}`;
}

function maxConcurrencyForBudget(budget: string): number {
  const category = budget.split(':', 1)[0] as WorkspaceRequestCategory;
  return CATEGORY_MAX_CONCURRENCY[category] ?? DEFAULT_MAX_CONCURRENCY;
}

function compareTaskPriority(
  left: CoordinatorTask<unknown>,
  right: CoordinatorTask<unknown>
): number {
  const priority =
    PRIORITY_ORDER[left.descriptor.priority] -
    PRIORITY_ORDER[right.descriptor.priority];
  return priority === 0 ? left.enqueuedAt - right.enqueuedAt : priority;
}

function raceWithSignal<T>(
  promise: Promise<T>,
  signal: AbortSignal | undefined
): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(signal.reason);
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      signal.addEventListener('abort', () => reject(signal.reason), {
        once: true,
      });
    }),
  ]);
}

function readRetryAfterHeader(reason: unknown): string | undefined {
  if (!isRecord(reason)) return undefined;
  const headers = reason.headers;
  if (!isHeaderReader(headers)) return undefined;
  return headers.get('Retry-After') ?? undefined;
}

function readRetryAfterValue(reason: unknown): string | number | undefined {
  if (!isRecord(reason)) return undefined;
  const value = reason.retryAfter;
  return typeof value === 'string' || typeof value === 'number'
    ? value
    : undefined;
}

function readRetryAfterMsValue(reason: unknown): string | number | undefined {
  if (!isRecord(reason)) return undefined;
  const value = reason.retryAfterMs;
  return typeof value === 'string' || typeof value === 'number'
    ? value
    : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function isHeaderReader(value: unknown): value is Pick<Headers, 'get'> {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof (value as Pick<Headers, 'get'>).get === 'function'
  );
}
